// ═══════════════════════════════════════════════════════════════
// PIXALLOY — Keyboard Handler Hook
// ═══════════════════════════════════════════════════════════════
// Extracted from PixAlloy.jsx. Registers keydown/keyup listeners.
// All dependencies passed directly — no context, no shared state.
// ═══════════════════════════════════════════════════════════════

import { useEffect } from "react";
import { C, T } from "../engine/index.js";
import { hx } from "../ui/components.jsx";
import { KEY_MAP } from "../tools/toolDefs.jsx";
import { penApplyTransform, transformPathList, pathListBounds, closePath, mkPt } from "../tools/penTool.js";

export function useKeyboard({
  // Refs (read directly, never through state)
  toolR, penUndoRef, penRedoRef, penPathRef, penDragRef, activePathIdxRef,
  bcolRef, bgColRef, dirtyRef,
  polyRef, sd, drag, eng,
  panState, rKeyHeldRef, spaceHeldRef, toolBeforeSpaceRef,
  // Callbacks
  penUndo, penRedo, setPenPathWithUndo, setPenPath, setPenPreset, setActivePathIdx,
  pc, fu, wp, setTool, rbActIdx, rbHasTfm,
  applyVPTransform, zoomToFit,
  doExport, doExportPSD, resetToSplash,
  setShowRulers, setSaveGuard, setShowText, setActiveMenu,
  setRadial, setSelCtxMenu, setAltHeld,
}) {
  useEffect(() => {
    const handleKey = e => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;

      if (e.ctrlKey || e.metaKey) {
        if (e.key === "z") {
          e.preventDefault();
          if (toolR.current === T.PEN && penUndoRef.current.length > 0) { penUndo(); }
          else { pc(C.UNDO); fu(); }
        }
        if (e.key === "y" || (e.shiftKey && e.key === "Z")) {
          e.preventDefault();
          if (toolR.current === T.PEN && penRedoRef.current.length > 0) { penRedo(); }
          else { pc(C.REDO); fu(); }
        }
        if (e.key === "c") { e.preventDefault(); pc(C.COPY); }
        if (e.key === "x") { e.preventDefault(); pc(C.CUT); fu(); }
        if (e.key === "v") { e.preventDefault(); pc(C.PASTE, { x: 0, y: 0 }); fu(); }
        if (e.key === "a") { e.preventDefault(); pc(C.SEL_ALL); }
        if (e.key === "d") { e.preventDefault(); pc(C.SEL_CLR); }
        if (e.shiftKey && e.key === "I") { e.preventDefault(); pc(C.SEL_INV); fu(); }
        if (e.key === "=" || e.key === "+") { e.preventDefault(); panState.current.zoom = Math.min(panState.current.zoom * 1.25, 8); applyVPTransform(); }
        if (e.key === "-") { e.preventDefault(); panState.current.zoom = Math.max(panState.current.zoom / 1.25, 0.1); applyVPTransform(); }
        if (e.key === "0") { e.preventDefault(); zoomToFit(); } // Ctrl+0 and Ctrl+Alt+0 both fit
        if (e.key === "1") { e.preventDefault(); panState.current.zoom = 1; panState.current.panX = 0; panState.current.panY = 0; applyVPTransform(); }
        if (e.key === "j") { e.preventDefault(); pc(C.DUPLICATE_LAYER, { idx: rbActIdx() }); fu(); }
        if (e.key === "e") { e.preventDefault(); doExport({ l: "PNG", m: "image/png", e: "png" }); }
        if (e.shiftKey && (e.key === "s" || e.key === "S")) { e.preventDefault(); doExportPSD(); }
        if (e.key === "r") { e.preventDefault(); setShowRulers(v => !v); }
        if (e.key === "n") { e.preventDefault(); setSaveGuard({ action: "new" }); return; }
        if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); const { r, g, b } = hx(bgColRef.current); pc(C.FILL_COLOR, { r, g, b, label: "BG" }); fu(); }
        return;
      }

      if (KEY_MAP[e.key]) setTool(KEY_MAP[e.key]);

      // Home key — fit to screen (1.3)
      if (e.key === "Home") { e.preventDefault(); zoomToFit(); }

      if (e.key === "Escape") {
        polyRef.current = []; sd.current = null;
        pc(C.SEL_CLR); pc(C.TFM_CANCEL);
        setShowText(false); setActiveMenu(null); setRadial(null); setSelCtxMenu(null);
        setPenPath([]); penDragRef.current = null; setPenPreset("none"); drag.current = null;
      }

      if (e.key === "Delete" || e.key === "Backspace") {
        // Pen tool: Delete removes the active path from the PathList
        if (toolR.current === T.PEN && penPathRef.current.length > 0) {
          e.preventDefault();
          const pathList = penPathRef.current;
          const activePI = activePathIdxRef.current != null ? activePathIdxRef.current : (pathList.length - 1);
          if (pathList.length <= 1) {
            setPenPathWithUndo([]);
          } else {
            setPenPathWithUndo(pl => pl.filter((_, i) => i !== activePI));
            // Clamp activePathIdx
            const newIdx = activePI >= pathList.length - 1 ? pathList.length - 2 : activePI;
            setActivePathIdx(Math.max(0, newIdx));
          }
          penDragRef.current = null;
        } else if (e.altKey) {
          const { r, g, b } = hx(bcolRef.current); pc(C.FILL_COLOR, { r, g, b, label: "FG" }); fu();
        } else if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          const { r, g, b } = hx(bgColRef.current); pc(C.FILL_COLOR, { r, g, b, label: "BG" }); fu();
        } else {
          pc(C.LAYER_CLR); fu();
        }
      }

      if (e.key === "Enter") {
        if (toolR.current === T.PEN && penPathRef.current.length > 0) {
          const pathList = penPathRef.current;
          const activePI = activePathIdxRef.current != null ? activePathIdxRef.current : (pathList.length - 1);
          const activePath = pathList[activePI];
          if (activePath && activePath.points.length >= 3 && !activePath.closed) {
            setPenPathWithUndo(pl => pl.map((pa, i) => i === activePI ? closePath(pa) : pa));
            penDragRef.current = null;
          }
        } else if (rbHasTfm()) {
          if (eng.current?.s.tfm?.selOnly) { pc(C.TFM_SEL_APPLY); fu(); }
          else { pc(C.TFM_APPLY); fu(); }
        }
      }

      // Arrow key nudging
      if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
        const dy = e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0;
        if (toolR.current === T.PEN && penPathRef.current.length > 0) {
          setPenPathWithUndo(transformPathList(penPathRef.current, (x, y) => ({ x: x + dx, y: y + dy })));
        } else if (toolR.current === T.MOVE || toolR.current === T.PEN) {
          pc(C.MOVE, { dx, dy }); fu();
        }
      }
    };

    const handleKeyUp = e => {
      if (e.key === "Alt") setAltHeld(false);
      if (e.key === "r" || e.key === "R") rKeyHeldRef.current = false;
      // Spacebar pan release (1.2) — restore previous tool
      if (e.key === " " && spaceHeldRef?.current) {
        spaceHeldRef.current = false;
        if (toolBeforeSpaceRef?.current != null) {
          setTool(toolBeforeSpaceRef.current);
          toolBeforeSpaceRef.current = null;
        }
      }
    };

    const handleKeyDown2 = e => {
      if (e.key === "Alt") setAltHeld(true);
      if (e.key === "r" || e.key === "R") { if (!e.ctrlKey && !e.metaKey) rKeyHeldRef.current = true; }
      // Spacebar temporary pan (1.2) — only activate if not in an input and not mid-stroke
      if (e.key === " " && spaceHeldRef && !spaceHeldRef.current) {
        if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
        // Don't activate if a stroke is in progress (drag active)
        if (sd?.current || drag?.current) return;
        e.preventDefault();
        spaceHeldRef.current = true;
        toolBeforeSpaceRef.current = toolR.current;
        setTool(T.PAN);
      }
    };

    window.addEventListener("keydown", handleKey);
    window.addEventListener("keydown", handleKeyDown2);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKey);
      window.removeEventListener("keydown", handleKeyDown2);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [setTool, pc, fu, applyVPTransform]);
}
